import React, { useState } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { resolveMediaUrl } from '../../../hooks/useStreamBootstrap.js';
import VinylSleeve, { coverLabel } from '../VinylSleeve.jsx';
import { NAME_PAINT_SURFACES } from '../../../_helpers/namePaint.js';
import PaintedName from './PaintedName.jsx';
import '../../../styles/NowPlayingCard.css';

/** The release date ("December 1, 2015"), read as a calendar date whatever the viewer's zone. */
const RELEASE_DATE_STYLE = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' };
const formatRelease = (value, format) => (value ? format.date(`${value}T00:00:00Z`, RELEASE_DATE_STYLE) : '');

/**
 * The cover as the shared record sleeve (ENG-769): while a song plays the
 * record is out and spinning; between songs it is tucked in and still.
 */
const Artwork = ({ src, fallbackLabel, playing }) => (
  <div className="now-playing-art" aria-hidden="true">
    <VinylSleeve
      artworkUrl={src || null}
      fallbackLabel={fallbackLabel}
      className="now-playing-sleeve"
      testId="now-playing-sleeve"
      active={playing}
    />
  </div>
);

/** How long the playing record may spin by itself (WCAG 2.2.2, ENG-842). */
export const SPIN_BUDGET_MS = 4800;

/**
 * How many turns at `spinMs` fit the spin budget: whole turns when one fits,
 * otherwise the fraction of one turn that does (the record rests mid-turn).
 */
export const spinIterations = (spinMs) => {
  const turns = SPIN_BUDGET_MS / spinMs;
  return turns >= 1 ? Math.floor(turns) : Number(turns.toFixed(3));
};

/**
 * A now-playing card skin's look (`cardSkinLook`, ENG-735) as the card's
 * classes and style: the accent colour frames and washes the card, the spin
 * speed drives the record, and the skin's image is a layer behind the details.
 */
const skinProps = (skin) => {
  if (!skin) return { className: '', style: undefined };
  const style = {};
  if (skin.accentColor) style['--now-playing-skin-accent'] = skin.accentColor;
  if (skin.spinMs) {
    style['--vinyl-sleeve-spin-duration'] = `${skin.spinMs}ms`;
    style['--vinyl-sleeve-spin-iterations'] = String(spinIterations(skin.spinMs));
  }
  return {
    className: ` now-playing-card--skinned now-playing-card--skin-${skin.rarity}`,
    style: Object.keys(style).length ? style : undefined,
  };
};

const SkinLayer = ({ skin }) => (skin?.imageUrl ? (
  <img className="now-playing-skin-image" src={skin.imageUrl} alt="" aria-hidden="true" data-testid="now-playing-skin-image" />
) : null);

/**
 * The "Seeing BackRowBecky's view · Back to mine" pill on the card's top edge
 * while another listener's skin is previewed (mockup 35, ENG-735). The
 * mockup's circled count has no data behind it yet and is left out.
 */
const PreviewPill = ({ preview }) => {
  const { t } = useLocale();
  return (
    <div className="now-playing-preview-pill" data-testid="now-playing-preview-pill">
      <span className="now-playing-preview-text" role="status">
        {t('stream.nowPlaying.preview.seeing', { name: preview.name })}
      </span>
      <button type="button" className="now-playing-preview-back" onClick={preview.onBack}>
        {t('stream.nowPlaying.preview.backToMine')}
      </button>
    </div>
  );
};

/**
 * "Trying on Event Horizon · this song" on the card's top edge while this
 * viewer tries a card skin on (mockup 36, ENG-737). Taking it off is on the
 * try-on panel in the chat.
 */
const TryOnPill = ({ tryOn }) => {
  const { t } = useLocale();
  return (
    <div className="now-playing-preview-pill now-playing-try-on-pill" data-testid="now-playing-try-on-pill">
      <span className="now-playing-preview-text" role="status">
        {t('stream.nowPlaying.tryOn', { name: tryOn.name })}
      </span>
    </div>
  );
};

/**
 * The bottom-left card of the stream page (mockup 01, ENG-667): vinyl-style
 * artwork and the playing song's details. The chevron collapses it to an
 * artwork-only tile so the setlist rail gets the room (mockup 02, ENG-669);
 * the rail measures itself and shows more cards. Collapsing is independent of
 * paging the rail.
 *
 * Between songs (`upNext`, mockup 17, ENG-699) the card dims and shows the
 * song that plays next as "UP NEXT · Come Together" with the same rows.
 *
 * `skin` is the card skin to draw (`cardSkinLook`), or null for the plain
 * card. `preview` (`{ name, onBack }`) marks it as another listener's skin
 * seen through "See what they see" (mockup 35, ENG-735) and shows the pill.
 * `tryOn` (`{ name }`) marks it as a skin this viewer is trying on (mockup
 * 36, ENG-737) and shows the "Trying on … · this song" pill instead.
 *
 * `decorative` draws it as a picture of the card (the skins shop's previews,
 * ENG-894): no collapse button, and the record stays in its sleeve until an
 * ancestor with `vinyl-sleeve-trigger` is hovered.
 */
const NowPlayingCard = ({ song, upNext = false, collapsed: collapsedProp, onToggleCollapsed, skin = null, preview = null, tryOn = null, decorative = false }) => {
  const { t, format } = useLocale();
  const [collapsedState, setCollapsedState] = useState(false);
  const collapsed = collapsedProp ?? collapsedState;
  const toggle = onToggleCollapsed ?? (() => setCollapsedState((v) => !v));
  const skinned = skinProps(skin);
  const skinAttrs = { style: skinned.style, 'data-skin-id': skin?.skinId || undefined };
  const decorations = (
    <>
      <SkinLayer skin={skin} />
      {tryOn ? <TryOnPill tryOn={tryOn} /> : preview && <PreviewPill preview={preview} />}
    </>
  );

  if (!song) {
    return (
      <section className={`now-playing-card now-playing-card--empty${skinned.className}`} {...skinAttrs} data-testid="now-playing-card" aria-label={t('stream.nowPlaying.label')}>
        {decorations}
        <p className="now-playing-empty">{t('stream.nowPlaying.empty')}</p>
      </section>
    );
  }

  const artwork = resolveMediaUrl(song.artworkUrl);
  const fallbackLabel = coverLabel(song.album || song.title, t('publicSongList.card.coverFallback'));
  const playing = !upNext;
  const spinning = playing && !decorative;
  // A new song remounts the sleeve so its bounded spin plays again.
  const artworkKey = `${song.entryId ?? song.songId ?? song.title}:${playing ? 'playing' : 'next'}`;
  const toggleButton = (
    <button
      type="button"
      className="now-playing-toggle"
      onClick={toggle}
      aria-expanded={!collapsed}
      aria-label={collapsed ? t('stream.nowPlaying.expand') : t('stream.nowPlaying.collapse')}
    >
      {collapsed
        ? <FaChevronRight className="rtl-mirror" aria-hidden="true" />
        : <FaChevronLeft className="rtl-mirror" aria-hidden="true" />}
    </button>
  );

  if (collapsed) {
    return (
      <section className={`now-playing-card now-playing-card--collapsed${upNext ? ' now-playing-card--up-next' : ''}${skinned.className}`} {...skinAttrs} data-testid="now-playing-card" aria-label={upNext ? t('stream.nowPlaying.upNextLabel') : t('stream.nowPlaying.label')}>
        {decorations}
        <Artwork key={artworkKey} src={artwork} fallbackLabel={fallbackLabel} playing={playing} />
        <span className="now-playing-sr">{song.title}</span>
        {toggleButton}
      </section>
    );
  }

  const released = formatRelease(song.releaseDate, format);
  const rows = [
    ['artist', song.artist],
    ['album', song.album],
    ['released', released],
    ['request', song.requestedBy],
    ['amount', song.amount > 0 ? format.usd(song.amount, { cents: 'never' }) : ''],
  ].filter(([, value]) => value);

  return (
    <section className={`now-playing-card${upNext ? ' now-playing-card--up-next' : ''}${skinned.className}`} {...skinAttrs} data-testid="now-playing-card" aria-label={upNext ? t('stream.nowPlaying.upNextLabel') : t('stream.nowPlaying.label')}>
      {decorations}
      <Artwork key={artworkKey} src={artwork} fallbackLabel={fallbackLabel} playing={spinning} />
      <div className="now-playing-body">
        <div className="now-playing-title-row">
          <h2 className="now-playing-title">
            {upNext ? t('stream.nowPlaying.upNext', { title: song.title }) : song.title}
          </h2>
          {!decorative && toggleButton}
        </div>
        <dl className="now-playing-rows">
          {rows.map(([key, value]) => (
            <div key={key} className={`now-playing-row now-playing-row--${key}`}>
              <dt>{t(`stream.nowPlaying.rows.${key}`)}</dt>
              <dd>
                {key === 'request'
                  ? <PaintedName name={value} paint={song.requestedByPaint} surface={NAME_PAINT_SURFACES.nowPlaying} />
                  : value}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

export default NowPlayingCard;
